import {
  useEffect,
  useState,
  useCallback,
  useContext,
  useRef,
  useMemo,
} from "react";
import {
  DialogContent,
  Paper,
  Button,
  Dialog,
  Fab,
  TextField,
  Tooltip,
  Typography,
  IconButton,
  Card,
  Box,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import Draggable from "react-draggable";
import { useStyles, paperPropsBigFixedBdlVnt } from "../../../css/muiStyle";
import {
  AlertCompo,
  BackdropCompo,
  NoTableDataComponent,
  RaccBtnStatic,
} from "../../../js/components";
import { ValidatorForm } from "react-material-ui-form-validator";
import { Close } from "@material-ui/icons";
import Iconn from "@mdi/react";
import { mdiCardsOutline, mdiDelete, mdiPen, mdiPhone } from "@mdi/js";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import { withStyles } from "@material-ui/core/styles";
import CardMedia from "@material-ui/core/CardMedia";
import ScrollBarDynamic from "../../../compo/ScrollBarDynamic";
import ParamContext from "../../../Contexte";
import { Autocomplete, Skeleton } from "@material-ui/lab";
import { ReactComponent as TransfertSvg } from "../../../img/icons/animatedTransfert.svg";
import OrderHistoryDialog from "./vnt_cpt_addBdl_histo";

// Constants
const NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const LABEL_TEXT = "Qte";
const ADD_TITLE = "Charger des bundles";
const SERVICE = "_rtl.php";

// Phone number configuration with validation rules
const PHONE_CONFIG = {
  BUNDLE: {
    prefixes: ["05", "06", "07"],
    maxLength: 10,
    type: "bundle",
    validation: {
      0: ["0"], // First digit must be 0
      1: ["5", "6", "7"], // Second digit must be 5,6,7
    },
  },
  IDOOM: {
    prefixes: ["02", "03", "04"],
    maxLength: 9,
    type: "idoom",
    validation: {
      0: ["0"], // First digit must be 0
      1: ["2", "3", "4"], // Second digit must be 2,3,4
    },
  },
  FOUR_G: {
    prefixes: ["213", "047"],
    maxLength: 12,
    type: "4g",
    validation: {
      0: ["2"], // First digit must be 2
      1: ["1"], // Second digit must be 1
      2: ["3"], // Third digit must be 3
      3: ["4"], // Fourth digit must be 4
      4: ["7"], // Fifth digit must be 7
    },
  },
};

// Styled Components
const styles = (theme) => ({
  root: {
    margin: 0,
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: 0,
    color: "white",
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle className={classes.root} {...other}>
      <Typography>{children}</Typography>
      {onClose && (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <Close />
        </IconButton>
      )}
    </MuiDialogTitle>
  );
});

const PaperComponent = (props) => (
  <Draggable
    handle="#draggable-dialog-title"
    cancel={'[class*="MuiDialogContent-root"]'}
  >
    <Paper {...props} />
  </Draggable>
);

// Helper Functions
const getPhoneNumberType = (phoneNumber) => {
  if (!phoneNumber || phoneNumber.length < 2) return null;

  // Check for 4G first (starts with 213)
  if (phoneNumber.startsWith("213")) {
    return PHONE_CONFIG.FOUR_G;
  }

  // Check first two digits for other types
  const firstTwoDigits = phoneNumber.substring(0, 2);

  if (PHONE_CONFIG.BUNDLE.prefixes.includes(firstTwoDigits)) {
    return PHONE_CONFIG.BUNDLE;
  }

  if (PHONE_CONFIG.IDOOM.prefixes.includes(firstTwoDigits)) {
    // Special case: if number starts with 047, it's 4G
    if (phoneNumber.startsWith("047")) {
      return PHONE_CONFIG.FOUR_G;
    }
    return PHONE_CONFIG.IDOOM;
  }

  return null;
};

// Enhanced validation function for digit-by-digit control
const validateNextDigit = (currentNumber, nextDigit) => {
  const newNumber = currentNumber + nextDigit;
  const length = newNumber.length;

  // If empty, only allow 0 or 2 as first digit
  if (length === 1) {
    return ["0", "2"].includes(nextDigit);
  }

  // Determine potential phone type based on current input
  if (length === 2) {
    const firstDigit = newNumber[0];
    const secondDigit = newNumber[1];

    if (firstDigit === "0") {
      // If first digit is 0, second digit can be 2,3,4,5,6,7
      return ["2", "3", "4", "5", "6", "7"].includes(secondDigit);
    } else if (firstDigit === "2") {
      // If first digit is 2, second digit must be 1 (for 213...)
      return secondDigit === "1";
    }
  }

  if (length === 3) {
    const firstThree = newNumber.substring(0, 3);

    if (firstThree === "213") {
      return true; // 213 is valid start for 4G
    }

    // For numbers starting with 0, after first 2 digits, any digit is allowed
    if (newNumber[0] === "0") {
      const secondDigit = newNumber[1];
      // Validate that the first two digits form a valid prefix
      const validSecondDigits = ["2", "3", "4", "5", "6", "7"];
      return validSecondDigits.includes(secondDigit);
    }
  }

  if (length === 4) {
    // For 4G numbers (213X), the fourth digit must be 4
    if (newNumber.startsWith("213")) {
      return nextDigit === "4";
    }

    // For other types, any digit is allowed after the prefix
    return true;
  }

  if (length === 5) {
    // For 4G numbers (2134X), the fifth digit must be 7
    if (newNumber.startsWith("2134")) {
      return nextDigit === "7";
    }

    // For other types, any digit is allowed after the prefix
    return true;
  }

  // For lengths beyond 5, any digit is allowed (we've validated the prefix)
  if (length > 5) {
    return true;
  }

  return false;
};

// Get allowed digits for the current position
const getAllowedDigits = (currentNumber) => {
  const length = currentNumber.length;

  if (length === 0) {
    // First digit: only 0 or 2
    return ["0", "2"];
  }

  if (length === 1) {
    const firstDigit = currentNumber[0];
    if (firstDigit === "0") {
      // Second digit after 0: 2,3,4,5,6,7
      return ["2", "3", "4", "5", "6", "7"];
    } else if (firstDigit === "2") {
      // Second digit after 2: must be 1
      return ["1"];
    }
  }

  if (length === 2) {
    const firstTwo = currentNumber.substring(0, 2);
    if (firstTwo === "21") {
      // Third digit after 21: must be 3
      return ["3"];
    }
    // For numbers starting with 0, any digit is allowed after prefix
    if (["02", "03", "04", "05", "06", "07"].includes(firstTwo)) {
      return ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
    }
  }

  if (length === 3) {
    const firstThree = currentNumber.substring(0, 3);
    if (firstThree === "213") {
      // Fourth digit after 213: must be 4
      return ["4"];
    }
    // For other types, any digit is allowed
    return ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
  }

  if (length === 4) {
    const firstFour = currentNumber.substring(0, 4);
    if (firstFour === "2134") {
      // Fifth digit after 2134: must be 7
      return ["7"];
    }
    // For other types, any digit is allowed
    return ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
  }

  // For positions beyond the prefix validation, all digits are allowed
  return ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
};

// Enhanced keyboard input validation
const validateKeyboardInput = (currentValue, newValue) => {
  // Allow backspace/delete
  if (newValue.length < currentValue.length) {
    return true;
  }

  // If pasting or multiple characters, validate each character
  if (newValue.length > currentValue.length + 1) {
    // Validate each character position
    for (let i = currentValue.length; i < newValue.length; i++) {
      const digit = newValue[i];
      const partialValue = newValue.substring(0, i);
      if (!validateNextDigit(partialValue, digit)) {
        return false;
      }
    }
    return true;
  }

  // Single character input
  const newDigit = newValue[newValue.length - 1];
  return validateNextDigit(currentValue, newDigit);
};

const getIdoomOffer = (phoneNumber) => {
  const phoneType = getPhoneNumberType(phoneNumber);
  if (!phoneType) return null;

  return phoneType.type === "4g" ? "4G" : "idoom";
};

const getOperatorIdFromPhone = (phoneNumber) => {
  const phoneType = getPhoneNumberType(phoneNumber);
  if (!phoneType) return null;

  if (phoneType.type === "bundle") {
    const secondDigit = phoneNumber.charAt(1);
    const operatorMap = {
      7: 3,
      6: [1, 11],
      5: 2,
    };
    return operatorMap[secondDigit] || null;
  }

  return "Idoom";
};

const filterFiltersByOperator = (filters, phoneNumber) => {
  if (!phoneNumber) return filters;

  const operatorId = getOperatorIdFromPhone(phoneNumber);

  return filters.filter((filter) => {
    if (!operatorId) return true;

    if (Array.isArray(operatorId)) {
      return operatorId.includes(filter.operator);
    }

    return filter.operator === operatorId;
  });
};

export default function Vnt_cpt_addBdl(props) {
  const param = useContext(ParamContext);
  const classes = useStyles();
  const Gfunc = require("../../../Gfunc");

  // Refs for direct DOM access
  const telInputRef = useRef(null);
  const qteInputRef = useRef(null);

  // State
  const [orders, setOrders] = useState([]);
  const [options, setOptions] = useState([]);
  const [loadingBundles, setLoadingBundles] = useState(false);
  const [loadingCards, setLoadingCards] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [errorNumber, setErrorNumber] = useState(false);
  const [errorQte, setErrorQte] = useState(false);
  const [focusedField, setFocusedField] = useState("qte");
  const [quantity, setQuantity] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [ticket, setTicket] = useState({
    tiketId: null,
    numero: "Nouveau ticket",
  });
  const [payer, setPayer] = useState(false);

  const [alertState, setAlertState] = useState({
    open: false,
    severity: "info",
    message: "",
  });
  const [needsSelection, setNeedsSelection] = useState(false);
  const [cards, setCards] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [filters, setFilters] = useState([]);
  const [activeBundleFilter, setActiveBundleFilter] = useState(null);
  const [sabbaqliDialogs, setSabbaqliDialogs] = useState([]);

  // Add this state to your component (with the other useState declarations)
  const [idoomDialog, setIdoomDialog] = useState({
    open: false,
    product: null,
    phoneNumber: "",
    offerInfo: null,
    loading: false,
  });

  // Add this function to handle opening the Idoom dialog
  const handleOpenIdoomDialog = async (product, phoneNumber) => {
    // Validate phone number first
    const phoneType = getPhoneNumberType(phoneNumber);

    if (!phoneType) {
      showAlert(
        "error",
        "Numéro de téléphone non reconnu. Formats acceptés: 02, 03, 04, 213",
      );
      return;
    }

    // Check if phone number is complete based on its type
    const isComplete =
      (phoneType.type === "idoom" && phoneNumber.length === 9) ||
      (phoneType.type === "4g" &&
        ((phoneNumber.startsWith("047") && phoneNumber.length === 10) ||
          (!phoneNumber.startsWith("047") && phoneNumber.length === 12)));

    if (!isComplete) {
      const requiredLength =
        phoneType.type === "idoom"
          ? 9
          : phoneType.type === "4g" && phoneNumber.startsWith("047")
            ? 10
            : 12;
      showAlert(
        "warning",
        `Numéro ${phoneType.type.toUpperCase()} incomplet. ${
          phoneNumber.length
        }/${requiredLength} chiffres.`,
      );
      return;
    }

    // Additional validation for 4G numbers (must start with 21347)
    if (
      phoneType.type === "4g" &&
      !phoneNumber.startsWith("21347") &&
      !phoneNumber.startsWith("047")
    ) {
      showAlert("error", "Numéro 4G invalide. Doit commencer par 21347");
      return;
    }

    // Determine if it's 4G or regular Idoom
    const is4G = phoneType.type === "4g";

    setIdoomDialog({
      open: true,
      product,
      phoneNumber,
      offerInfo: null,
      loading: true,
    });

    try {
      // Call the service to check Idoom offer
      const response = await Gfunc.axiosPostAction("_irh.php", "atvrf", {
        tel: phoneNumber,
        lte: is4G, // true for 4G, false for regular Idoom
        //productId: product.id || product.liberoId,
      });

      if (response.data.err && response.data.err !== "-1") {
        showAlert("error", response.data.err);
        setIdoomDialog((prev) => ({ ...prev, open: false }));
        return;
      }
      Gfunc.axiosResponse(response, () => {
        if (response.data.success && response.data.success === "0") {
          showAlert("error", response.data.message);
          setIdoomDialog((prev) => ({ ...prev, open: false }));
          return;
        }
        setIdoomDialog((prev) => ({
          ...prev,
          is4G: is4G,
          offerInfo: response.data,
          loading: false,
        }));
      });
    } catch (error) {
      console.error("Error checking Idoom offer:", error);
      showAlert("error", "Erreur lors de la vérification de l'offre");
      setIdoomDialog((prev) => ({ ...prev, open: false }));
    }
  };

  // Add this function to handle Idoom order submission
  const handleIdoomOrder = async () => {
    const { product, phoneNumber, offerInfo, is4G } = idoomDialog;
    if (!product || !phoneNumber) return;

    setIdoomDialog((prev) => ({ ...prev, open: false }));

    const date = new Date();
    const orderId = `IDOOM-${Date.now()}`;

    const formattedDate = `${Gfunc.formatDate(date)} ${date.toLocaleTimeString(
      "fr-FR",
      { hour12: false },
    )}`;
    setOrders((prev) => [
      {
        id: orderId,
        date_order: formattedDate,
        bundle: { id: product.liberoId, bundle: product.digit },
        stat: 1, // pending
        qte: 1,
        numero: phoneNumber,
        card: true,
      },
      ...prev,
    ]);
    try {
      const payload = {
        tel: phoneNumber,
        lte: is4G,
        ncli: offerInfo.nci,
        typ: offerInfo.typ,
        prodId: product.liberoId,
        session: Gfunc.irahaUser.sesIrh,
        isubu: Gfunc.irahaUser.idsubIrh,
        ...(process.env.NODE_ENV === "development" && {
          devDIGIT: "240526390073006",
        }),
      };

      const response = await Gfunc.axiosPostAction(
        "_irh.php",
        "atcnf",
        payload,
      );

      //setIdoomDialog((prev) => ({ ...prev, loading: false }));
      setIdoomDialog({
        open: false,
        product: null,
        phoneNumber: "",
        message: null,
        offerInfo: null,
        loading: false,
      });
      if (response.data.err && response.data.err !== "-1") {
        if (process.env.NODE_ENV === "development")
          handleAddTicket(
            product.digit,
            phoneNumber,
            product.prx,
            product.prixPublic,
            payer,
            true,
          );
        Gfunc.playSound("error");
        showAlert("error", response.data.err);
        setOrders((prev) =>
          prev.map((order) =>
            order.id === orderId ? { ...order, stat: 3 } : order,
          ),
        );
        return;
      }

      Gfunc.axiosResponse(response, () => {
        if (response.data.length) {
          return;
        }
        if (Array.isArray(response.data) && !response.data.length) {
          showAlert("error", "Le service ne repond pas");
          Gfunc.playSound("error");
        }
        if (
          Gfunc.isObject(response.data) &&
          response.data.success &&
          response.data.success === "0"
        ) {
          if (process.env.NODE_ENV === "development")
            handleAddTicket(
              product.digit,
              phoneNumber,
              product.prx,
              product.prixPublic,
              payer,
              true,
            );
          Gfunc.playSound("error");
          showAlert("error", response.data.message ?? "");
          setOrders((prev) =>
            prev.map((order) =>
              order.id === orderId
                ? { ...order, stat: 3, date_transfert: formattedDate }
                : order,
            ),
          );
          return;
        }
        if (
          Gfunc.isObject(response.data) &&
          response.data.success &&
          response.data.success === "1"
        )
          handleAddTicket(
            product.digit,
            phoneNumber,
            product.prx,
            product.prixPublic,
            payer,
            true,
          );
        Gfunc.playSound("success");
        setIdoomDialog({
          open: false,
          product: null,
          phoneNumber: "",
          message: null,
          offerInfo: null,
          loading: false,
        });
        setOrders((prev) =>
          prev.map((order) =>
            order.id === orderId
              ? { ...order, stat: 4, date_transfert: formattedDate }
              : order,
          ),
        );
      });
    } catch (error) {
      console.error("Error submitting Idoom order:", error);
      showAlert("error", "Erreur lors de la commande Idoom");
      setIdoomDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  // Add this function to close the Idoom dialog
  const handleCloseIdoomDialog = () => {
    setIdoomDialog({
      open: false,
      product: null,
      phoneNumber: "",
      message: null,
      offerInfo: null,
      loading: false,
    });
  };

  // Add this render function for the Idoom dialog (add it near your other render functions)
  const renderIdoomDialog = () => {
    const { open, product, phoneNumber, offerInfo, loading } = idoomDialog;

    if (!open || !product) return null;

    const phoneType = getPhoneNumberType(phoneNumber);
    const is4G = phoneType && phoneType.type === "4g";
    const offerName = offerInfo?.message || product.digit || product.nomFr;

    return (
      <Dialog
        open={open}
        onClose={handleCloseIdoomDialog}
        PaperProps={{
          style: {
            padding: "24px 32px",
            borderRadius: "12px",
            textAlign: "center",
            minWidth: "300px",
            maxWidth: "400px",
          },
        }}
      >
        <BackdropCompo open={loading} />

        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          style={{ gap: "16px", minWidth: "326px" }}
        >
          <Typography variant="h5" gutterBottom>
            Offre {is4G ? "4G: " : "Idoom: "} {product.digit}
          </Typography>
          <Card
            className="bdlCards"
            style={{
              width: "100%",
              boxShadow: 3,
              minHeight: "200px",
              borderRadius: "10px",
            }}
            elevation={4}
          >
            <CardMedia
              component="img"
              width="100%"
              height={"auto"}
              minHeight="200px"
              image={param.imgDigital + "prod_" + product.liberoId + ".png"}
              style={{
                padding: 0,
                objectFit: "contain",
                borderRadius: "20px",
              }}
            />
          </Card>

          {/* Phone Number */}
          <Typography
            style={{
              paddingTop: "8px",
              color: "var(--primary-main)",
              fontWeight: "600",
            }}
            variant="h4"
            gutterBottom
          >
            {Gfunc.formatPhoneNumber(phoneNumber)}
          </Typography>

          {/* Offer Name */}
          <Typography
            variant="h6"
            style={{
              color: "green",
              fontWeight: "bold",
            }}
          >
            {offerName}
          </Typography>

          {/* Additional offer info if available */}
          {offerInfo?.description && (
            <Typography variant="body2" style={{ marginBottom: 8 }}>
              {offerInfo.message}
            </Typography>
          )}

          {offerInfo?.validity && (
            <Typography variant="body2" style={{ marginBottom: 16 }}>
              Validité: <b>{offerInfo.validity}</b>
            </Typography>
          )}

          {/* Order Button */}
          <Button
            variant="contained"
            color="primary"
            style={{ fontSize: "18px" }}
            onClick={handleIdoomOrder}
            disabled={loading}
          >
            {loading ? "Traitement..." : "Confirmer la commande"}
          </Button>

          {/* Cancel Button */}
          <Button
            variant="outlined"
            color="secondary"
            style={{ fontSize: "14px" }}
            onClick={handleCloseIdoomDialog}
          >
            Annuler
          </Button>
        </Box>
      </Dialog>
    );
  };

  // Derived values using useMemo for optimization
  const phoneType = useMemo(
    () => getPhoneNumberType(phoneNumber),
    [phoneNumber],
  );

  const filteredFilters = useMemo(
    () => filterFiltersByOperator(filters, phoneNumber),
    [filters, phoneNumber],
  );

  const allowedDigits = useMemo(
    () =>
      focusedField === "tel"
        ? getAllowedDigits(phoneNumber)
        : ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"],
    [phoneNumber, focusedField],
  );

  const filteredBundles = useMemo(() => {
    let filtered = [...bundles];

    if (phoneNumber) {
      const operator = getOperatorIdFromPhone(phoneNumber);
      if (operator) {
        if (Array.isArray(operator)) {
          //  return operatorId.includes(filter.operator);
          filtered = filtered.filter((product) =>
            operator.includes(product.operator),
          );
        } else
          filtered = filtered.filter(
            (product) => product.operator === operator,
          );
      }
    }

    if (quantity) {
      const qteValue = parseInt(quantity, 10);

      filtered = filtered.filter(
        (product) =>
          (product.prix && product.prix === JSON.parse(qteValue)) ||
          product.prix == null,
      );
    }

    if (activeBundleFilter) {
      filtered = filtered.filter(
        (product) =>
          product.nomFr &&
          product.nomFr
            .toUpperCase()
            .includes(activeBundleFilter.toUpperCase()),
      );
    }

    return filtered;
  }, [bundles, phoneNumber, quantity, activeBundleFilter]);

  const filteredCards = useMemo(() => {
    let filtered = [];
    if (cards) filtered = [...cards];

    if (phoneNumber) {
      const offer = getIdoomOffer(phoneNumber);
      if (offer) {
        filtered = filtered.filter(
          (product) =>
            product.digit &&
            product.digit.toUpperCase().includes(offer.toUpperCase()),
        );
      }
    }

    if (quantity) {
      const qteValue = parseInt(quantity, 10);
      filtered = filtered.filter(
        (product) =>
          (product.prx &&
            Gfunc.extractNumber(product.digit) === JSON.parse(qteValue)) ||
          product.prx == null,
      );
    }

    return filtered;
  }, [cards, phoneNumber, quantity]);

  // Effects
  useEffect(() => {
    if (needsSelection) {
      handleFieldFocus("qte");
      setNeedsSelection(false);
    }
  }, [needsSelection]);

  // Auto-focus management
  useEffect(() => {
    if (!phoneNumber || !phoneType) return;
    //focus qte input when phone number is complete
    // const shouldFocusQte =
    //   (phoneType.type === "bundle" && phoneNumber.length === 10) ||
    //   (phoneType.type === "idoom" && phoneNumber.length === 9) ||
    //   (phoneType.type === "4g" && phoneNumber.length === 12);

    // if (shouldFocusQte && qteInputRef.current) {
    //   //  avoid state updates during render
    //   requestAnimationFrame(() => {
    //     qteInputRef.current.focus();
    //     setFocusedField("qte");
    //   });
    // }
  }, [phoneNumber, phoneType]);

  useEffect(() => {
    fetchBundles();
    fetchOrdersHistory();
    fetchCards();
    fetchFilters();
  }, []);

  useEffect(() => {
    if (props.tickets && props.ticketId) {
      const found = props.tickets.find((opt) => opt.tiketId === props.ticketId);
      if (found) setTicket(found);
    }
  }, [props.tickets, props.ticketId]);

  // Event Handlers
  const showAlert = useCallback((severity, message) => {
    setAlertState({ open: true, severity, message });
    setTimeout(() => setAlertState((prev) => ({ ...prev, open: false })), 5000);
  }, []);

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;

    // Enhanced validation for keyboard input
    if (value === "") {
      setPhoneNumber("");
      setErrorNumber(false);
      return;
    }

    // Check if it's a valid digit input
    if (!/^\d+$/.test(value)) {
      // Not a digit, prevent input
      e.preventDefault();
      return;
    }

    // Validate the new value against our rules
    if (validateKeyboardInput(phoneNumber, value)) {
      setPhoneNumber(value);
      setErrorNumber(false);
    } else {
      // Invalid input, show warning and prevent change
      showAlert("warning", `Séquence de chiffres non autorisée`);
      // Reset to previous valid value
      e.target.value = phoneNumber;
    }
  };

  // Enhanced keyboard event handler for direct key presses
  const handlePhoneKeyPress = useCallback(
    (e) => {
      if (focusedField !== "tel") return;
      const key = e.key;
      // Allow control keys (backspace, delete, tab, etc.)
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      // Allow navigation keys
      if (
        [
          "Backspace",
          "Delete",
          "Tab",
          "ArrowLeft",
          "ArrowRight",
          "ArrowUp",
          "ArrowDown",
          "Home",
          "End",
        ].includes(key)
      ) {
        return;
      }

      // Check if it's a digit
      if (!/^\d$/.test(key)) {
        // Not a digit, prevent input
        e.preventDefault();
        return;
      }

      // Validate the digit against current position
      if (!validateNextDigit(phoneNumber, key)) {
        e.preventDefault();
        // showAlert(
        //   "warning",
        //   `Le chiffre ${key} n'est pas autorisé à cette position`
        // );
      }
    },
    [phoneNumber, focusedField, showAlert],
  );

  // Enhanced paste handler
  const handlePhonePaste = useCallback(
    (e) => {
      if (focusedField !== "tel") return;

      const pasteData = e.clipboardData.getData("text");

      // Check if pasted data contains only digits
      if (!/^\d+$/.test(pasteData)) {
        e.preventDefault();
        showAlert("error", "Seuls les chiffres sont autorisés");
        return;
      }

      // Validate the entire pasted sequence
      if (!validateKeyboardInput(phoneNumber, phoneNumber + pasteData)) {
        e.preventDefault();
        showAlert(
          "warning",
          "La séquence collée contient des chiffres non autorisés",
        );
      }
    },
    [phoneNumber, focusedField, showAlert],
  );

  const handleQuantityChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setQuantity(value);
      setErrorQte(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const bundleFilters = await Gfunc.getJsonFile(
        param.imgBdl + "filtres/fltr_bundles.json",
      );

      let filtersArray = [];

      // Handle both array and object responses
      if (Array.isArray(bundleFilters)) {
        filtersArray = bundleFilters;
      } else if (bundleFilters && typeof bundleFilters === "object") {
        // Convert object to array
        filtersArray = Object.keys(bundleFilters).map((key) => ({
          id: key,
          ...bundleFilters[key],
        }));
      }

      if (filtersArray.length > 0) {
        setFilters(filtersArray);
      } else {
        console.warn("No valid filters found in JSON");
        setFilters([]);
      }
    } catch (error) {
      console.error("Error fetching filters:", error);
      setFilters([]);
    }
  };

  const fetchSuggestions = async () => {
    if (props.sugg.length === 0) {
      try {
        const response = await Gfunc.axiosGetAction("_vnt.php", "getqteat");
        if (response.data && response.data.err) {
          props.handleAlert(true, "error", response.data.err);
        } else {
          Gfunc.axiosResponse(response, () => {
            if (response.data.jData) {
              setOptions(response.data.jData ?? []);
              props.setSugg(response.data.jData ?? []);
            }
          });
        }
      } catch (error) {
        console.error("Error fetching suggestions:", error);
      }
    } else {
      setOptions(props.sugg);
      setNeedsSelection(true);
    }
  };

  const fetchBundles = async () => {
    try {
      setLoadingBundles(true);
      const response = await Gfunc.axiosGetAction(SERVICE, "getbdl");
      if (response.data && response.data.err) {
        props.handleAlert(true, "error", response.data.err);
      } else {
        Gfunc.axiosResponse(response, () => {
          if (response.data) {
            setBundles(response.data ?? []);
          }
        });
      }
    } catch (error) {
      console.error("Error fetching bundles:", error);
    } finally {
      setLoadingBundles(false);
    }
  };

  const fetchCards = async () => {
    try {
      setLoadingCards(true);
      const response = await Gfunc.axiosPostAction("_irh.php", "getdigitprd", {
        catId: 1,
      });
      if (response.data && response.data.err) {
        props.handleAlert(true, "error", response.data.err);
      } else {
        Gfunc.axiosResponse(response, () => {
          if (response.data) {
            setCards(response.data.jData ?? []);
          }
        });
      }
    } catch (error) {
      console.error("Error fetching bundles:", error);
    } finally {
      setLoadingCards(false);
    }
  };

  const fetchOrdersHistory = async () => {
    try {
      const response = await Gfunc.axiosGetAction("_rtl.php", "geto");
      if (response.data.err) {
        props.handleAlert(true, "error", response.data.err);
      } else {
        Gfunc.axiosResponse(response, () => {
          if (response.data) {
            setOrders(response.data.data ? response.data.data : []);
          }
        });
      }
    } catch (error) {
      console.error("Error fetching orders history:", error);
    } finally {
    }
  };

  const handleOpenDialog = () => {
    fetchSuggestions();
    setIsDialogOpen(true);
    setQuantity("");
    setPhoneNumber("");
    setActiveBundleFilter(null);
    setFocusedField("tel");
    setTimeout(() => {
      handleFieldFocus("tel");
    }, 100);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setOptions([]);
    setActiveBundleFilter(null);
    props.fetchTickets();
    props.Caisse();
    props.GetAtSoldes();
  };

  // Sabbaqli Dialog Management
  const openSabbaqliDialog = (sbqlData) => {
    const dialogId = Date.now().toString();
    setSabbaqliDialogs((prev) => [
      ...prev,
      { ...sbqlData, id: dialogId, open: true },
    ]);
  };

  const closeSabbaqliDialog = (dialogId) => {
    setSabbaqliDialogs((prev) =>
      prev.filter((dialog) => dialog.id !== dialogId),
    );
  };

  const handleSabbaqliChoice = async (dialogId, payAll) => {
    const dialog = sabbaqliDialogs.find((d) => d.id === dialogId);
    if (!dialog) return;

    const { oId, api, num, qte, bdlName, sbqQte } = dialog;
    const isDev = process.env.NODE_ENV === "development";
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const dev = isDev ? (obj) => obj : () => ({}); // automatically ignore dev attributes in prod

    try {
      const response3 = await Gfunc.axiosPostAction(
        SERVICE,
        api ? "order" : "sbql",
        {
          oId,
          payAll,
          session: Gfunc.irahaUser.sesIrh,
          isubu: Gfunc.irahaUser.idsubIrh,
          ...dev({ devDATA: { status: true } }),
        },
      );
      closeSabbaqliDialog(dialogId);
      if (response3.data.err && response3.data.err !== "-1") {
        Gfunc.playSound("error");
        showAlert("error", response3.data.err);
        setOrders((prevOrders) =>
          prevOrders.map((order) =>
            order.id === oId ? { ...order, stat: 3 } : order,
          ),
        );
        return;
      }

      Gfunc.axiosResponse(response3, async () => {
        const { api: newApi } = response3.data;
        const maxAttempts = 5;

        if (!newApi) await delay(param.refreshSec);

        let refreshResponse = await Gfunc.axiosPostAction(SERVICE, "refresh", {
          oId,
          ...dev({ devERROR: false, devSBQ: 50, devACTIV: true }),
        });

        let attempt = 0;
        while (
          refreshResponse.data &&
          attempt < maxAttempts &&
          !refreshResponse.data.data?.date_transfert
        ) {
          await delay(param.refreshSec);
          refreshResponse = await Gfunc.axiosPostAction(SERVICE, "refresh", {
            oId,
            ...dev({
              devDATE: Gfunc.formatDate(new Date()),
              devERROR: true,
              devSBQ: 0,
              devACTIV: true,
            }),
          });
          attempt++;
        }

        const refreshData = refreshResponse.data.data;

        setOrders((prevOrders) =>
          prevOrders.map((order) =>
            order.id === oId
              ? { ...order, ...refreshData, stat: refreshData.error ? 3 : 4 }
              : order,
          ),
        );

        if (!refreshData.error) {
          handleAddTicket(
            bdlName,
            num,
            refreshData.consumed,
            payAll ? sbqQte + qte : qte,
            payer,
          );
          Gfunc.playSound("success");
        } else {
          Gfunc.playSound("error");
          showAlert(
            "error",
            `Commande ${bdlName} - ${num} n'a pas été effectuée !`,
          );
        }

        // closeSabbaqliDialog(dialogId);
        // props.callBack?.();
      });
    } catch (err) {
      console.error("Error in SBQL processing:", err);
      showAlert("error", "Erreur lors du traitement SBQL.");
    }
  };

  const handleAddTicket = async (
    product,
    tel,
    quantity,
    price,
    payer,
    idoom,
  ) => {
    const requestData = {
      jourId: props.jourId,
      tiketId: ticket.tiketId ? ticket.tiketId : null,
      tel: tel,
      qte: parseInt(quantity),
      mtn: price,
      bdlname: product,
      defStock: parseInt(localStorage.getItem("stockId")),
      pay: payer,
      idoom: idoom ? idoom : false,
    };

    try {
      const response = await Gfunc.axiosPostAction(
        "_rtl.php",
        "addtb",
        requestData,
      );

      if (response.data.err && response.data.err !== "-1") {
        showAlert("error", response.data.err);
      } else {
        Gfunc.axiosResponse(response, async () => {
          if (response.data.jData) {
          }
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showAlert("error", "Erreur lors de l'ajout de la vente");
    }
  };

  const handleSelectBundle = (keySearch) => {
    setLoadingBundles(true);
    const newFilter = activeBundleFilter === keySearch ? null : keySearch;
    setActiveBundleFilter(newFilter);
    setLoadingBundles(false);
  };
  const getPhoneInputProps = () => {
    const phoneType = getPhoneNumberType(phoneNumber);

    return {
      maxLength: phoneType
        ? phoneNumber.startsWith("047")
          ? 10
          : phoneType.maxLength
        : 12,
      pattern: phoneType
        ? phoneType.type === "bundle"
          ? "[0][5-7][0-9]{8}"
          : phoneType.type === "idoom"
            ? "[0][2-4][0-9]{7}"
            : "[2][1][3][0-9]{8}"
        : ".*",
      placeholder: phoneType
        ? `${phoneType.type.toUpperCase()} - ${phoneType.maxLength} chiffres`
        : "",
    };
  };
  const phoneInputProps = getPhoneInputProps();
  // UI Helpers - UPDATED with controlled numeric pad
  const handleNumberInput = useCallback(
    (number) => {
      const isQuantityField = focusedField === "qte";
      const currentValue = isQuantityField ? quantity : phoneNumber;
      const setFunction = isQuantityField ? setQuantity : setPhoneNumber;
      const inputRef = isQuantityField ? qteInputRef : telInputRef;

      if (!inputRef.current) return;

      const input = inputRef.current;
      const start = input.selectionStart ?? currentValue.length;
      const end = input.selectionEnd ?? currentValue.length;

      // Build the new value by replacing the selected text
      const inserted = number.toString();
      const newValue =
        currentValue.slice(0, start) + inserted + currentValue.slice(end);

      // Validation & max length checks
      if (isQuantityField) {
        if (/^\d*$/.test(newValue)) {
          setFunction(newValue);
        }
      } else {
        // validate digit for position (keep your existing validator)
        if (!validateNextDigit(currentValue, inserted)) {
          showAlert(
            "warning",
            `Le chiffre ${number} n'est pas autorisé à cette position`,
          );
          return;
        }

        // Use resulting length, not current length.
        const maxLen = phoneInputProps?.maxLength
          ? parseInt(phoneInputProps.maxLength, 10)
          : undefined;
        if (typeof maxLen === "number") {
          if (newValue.length <= maxLen) {
            setFunction(newValue);
          } else {
            showAlert("warning", `Longueur maximale atteinte (${maxLen}).`);
            return;
          }
        } else {
          // no maxLength configured
          setFunction(newValue);
        }
      }

      // Move cursor after inserted number (after state update)
      setTimeout(() => {
        const cursorPos = start + inserted.length;
        // clamp to newValue.length to be safe
        input.setSelectionRange(
          Math.min(cursorPos, newValue.length),
          Math.min(cursorPos, newValue.length),
        );
      }, 0);
    },
    [focusedField, quantity, phoneNumber, showAlert, phoneInputProps],
  );

  const handleFieldFocus = useCallback((fieldName) => {
    setFocusedField(fieldName);

    const inputRef = fieldName === "qte" ? qteInputRef : telInputRef;
    if (inputRef.current) {
      inputRef.current.focus();
      setTimeout(() => {
        inputRef.current.select();
      }, 0);
    }
  }, []);

  const handleClearCurrentField = () => {
    if (focusedField === "qte") {
      setQuantity("");
      if (qteInputRef.current) {
        qteInputRef.current.focus();
      }
    } else {
      setPhoneNumber("");
      if (telInputRef.current) {
        telInputRef.current.focus();
      }
    }
  };

  const handleOrder = async (product) => {
    const phoneValid = Gfunc.getPhoneInfo(phoneNumber).valid;
    const isDev = process.env.NODE_ENV === "development";

    // Validation initiale
    if (!phoneValid) {
      setErrorNumber(true);
      telInputRef.current.focus();

      const message =
        !quantity && (product.usem || product.prix == null)
          ? "Veuillez insérer le numéro et le montant d'abord."
          : "Veuillez insérer le numéro d'abord.";

      showAlert("error", message);
      return;
    }

    setErrorNumber(false);

    if (!quantity && (product.usem || product.prix == null)) {
      setErrorQte(true);
      qteInputRef.current.focus();
      showAlert("error", "Veuillez insérer le numéro et le montant d'abord.");
      return;
    }

    setErrorQte(false);
    // Confirmation avant de continuer
    const confirmed = window.confirm(
      "Êtes-vous sûr de passer cette commande ?",
    );
    if (!confirmed) return; // Annuler si l'utilisateur refuse

    const qteFinal =
      quantity && parseInt(quantity, 10) > 0
        ? parseInt(quantity, 10)
        : product.prix || 0;
    console.log(product);
    const baseOrderPayload = {
      bId: product.liberoId,
      qte: qteFinal,
      // idO: product.operator,
      num: phoneNumber,
      session: Gfunc.irahaUser.sesIrh,
      isubu: Gfunc.irahaUser.idsubIrh,
      ...(isDev && { devID: { err: "32" }, devAPI: false }),
    };

    let response = await Gfunc.axiosPostAction(
      SERVICE,
      "order",
      baseOrderPayload,
    );
    telInputRef.current.focus();
    setTimeout(() => {
      telInputRef.current.select();
    }, 0);
    setQuantity("");

    if (response.data.err && response.data.err !== "-1") {
      showAlert("error", response.data.err);
      return;
    }

    // 🔁 Gestion de l’erreur 32 (re-tentative)
    if (hasError32(response.data.data)) {
      response = await retryOrder(baseOrderPayload, isDev);
      if (!response) return;
    }

    await handleOrderResponse(response, product, qteFinal, isDev);
  };

  // Helper: check for err 32
  const hasError32 = (data) => data?.err?.includes("32");

  // Helper: retry order when err 32
  const retryOrder = async (payload, isDev) => {
    let attempt = 0;
    const maxAttempts = 5;

    while (attempt < maxAttempts) {
      console.warn("Exception 32 détectée — relance de la commande...");
      const retryPayload = {
        ...payload,
        ...(isDev && { devID: 1, devAPI: false }),
      };

      const res = await Gfunc.axiosPostAction(SERVICE, "order", retryPayload);

      if (!hasError32(res.data.data)) return res;

      attempt++;
    }

    showAlert("error", "Erreur persistante (32) après plusieurs tentatives.");
    return null;
  };

  // 🧩 Helper: handle order response + refresh logic
  const handleOrderResponse = async (response, product, qteFinal, isDev) => {
    if (!response.data || Gfunc.isEmpty(response.data)) return;

    const date = new Date();
    const newOrder = {
      id: response.data.data,
      date_order: `${Gfunc.formatDate(date)} ${date.toLocaleTimeString(
        "fr-FR",
        {
          hour12: false,
        },
      )}`,
      bundle: { id: product.bundleId, bundle: product.nomFr },
      stat: 1,
      qte: qteFinal,
      numero: phoneNumber,
    };

    setOrders((prev) => [newOrder, ...prev]);

    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const maxAttempts = 5;

    const baseRefreshPayload = {
      oId: response.data.data,
      ...(isDev && { devERROR: "error", devSBQ: 50, devACTIV: true }),
    };

    // 🕐 wait if needed before refresh
    if (response.data.api === false || response.data.api == null) {
      await delay(param.refreshSec);
    }

    let response2 = await Gfunc.axiosPostAction(
      SERVICE,
      "refresh",
      baseRefreshPayload,
    );
    let data2 = response2.data.data;

    if (data2 && !data2.activ) return removeOrder(response.data.data);

    // 🔁 Retry refresh if date_transfert missing
    let attempt = 0;
    while (data2 && attempt < maxAttempts && !data2.date_transfert) {
      await delay(param.refreshSec);
      const retryRefreshPayload = {
        ...baseRefreshPayload,
        ...(isDev && {
          devDATE: `${Gfunc.formatDate(new Date())} 11:00:02`,
          devERROR: "Error",
          devSBQ: 50,
          devACTIV: true,
        }),
      };
      response2 = await Gfunc.axiosPostAction(
        SERVICE,
        "refresh",
        retryRefreshPayload,
      );
      data2 = response2.data.data;
      attempt++;
    }

    // 🧾 Check order final state
    if (data2 && !data2.activ) return removeOrder(response.data.data);

    if (data2 && data2.sbqQte && data2.sbqQte > 0 && data2.activ) {
      openSabbaqliDialog({
        num: phoneNumber,
        qte: qteFinal,
        bdl: product.bundleId,
        bdlName: product.nomFr,
        sbqQte: data2.sbqQte,
        oId: response.data.data,
        api: response.data.api,
      });
      return;
    }

    if (
      data2 &&
      data2.date_transfert &&
      data2.stat === 4 &&
      !data2.error &&
      data2.activ
    ) {
      handleAddTicket(
        product.nomFr,
        phoneNumber,
        data2.consumed,
        qteFinal,
        payer,
      );
    }
    Gfunc.playSound(!data2 ? "error" : "success");
    // Update existing order with new info
    setOrders((prev) =>
      prev.map((order) =>
        order.id === response.data.data
          ? {
              ...order,
              ...data2,
              date_transfert: data2 ? data2.date_transfert : "",
              stat: !data2 ? 3 : data2.stat,
            }
          : order,
      ),
    );
  };

  // 🧩 Helper: remove order from state
  const removeOrder = (id) => {
    setOrders((prev) => prev.filter((o) => o.id !== id));
  };

  // Render Helpers - UPDATED with controlled numeric pad
  const renderPriceSuggestions = () => (
    <div className="suggestedPrices">
      {options.map((price, index) => (
        <Button
          key={index}
          variant="contained"
          color="secondary"
          className={classes.squarBtn}
          onClick={() => {
            setQuantity(price);
            handleFieldFocus("qte");
          }}
        >
          {new Intl.NumberFormat().format(price)}
        </Button>
      ))}
    </div>
  );

  const renderNumberPad = () => (
    <div className="numberKeyboard">
      {NUMBERS.map((number) => {
        const isDisabled =
          focusedField === "tel" && !allowedDigits.includes(number.toString());

        return (
          <Fab
            key={number}
            tabIndex="-1"
            className={classes.sideBtnCalc}
            variant="extended"
            color={isDisabled ? "default" : "secondary"}
            onClick={() => !isDisabled && handleNumberInput(number)}
            disabled={isDisabled}
            style={{
              opacity: isDisabled ? 0.4 : 1,
              cursor: isDisabled ? "not-allowed" : "pointer",
            }}
          >
            {number}
          </Fab>
        );
      })}
    </div>
  );

  const renderControlPad = () => (
    <div className="zeroKeyboard">
      <Tooltip title="Sélectionner le téléphone">
        <Fab
          className={classes.sideBtnCalcSide}
          tabIndex="-1"
          variant="extended"
          color="secondary"
          onClick={() => handleFieldFocus("tel")}
        >
          <Iconn path={mdiPhone} color="black" size="20px" />
        </Fab>
      </Tooltip>

      <Fab
        className={classes.sideBtnCalc}
        tabIndex="-1"
        variant="extended"
        color={
          focusedField === "tel" && !allowedDigits.includes("0")
            ? "default"
            : "secondary"
        }
        onClick={() => {
          if (focusedField === "tel" && !allowedDigits.includes("0")) {
            showAlert(
              "warning",
              "Le chiffre 0 n'est pas autorisé à cette position",
            );
          } else {
            handleNumberInput(0);
          }
        }}
        disabled={focusedField === "tel" && !allowedDigits.includes("0")}
        style={{
          opacity:
            focusedField === "tel" && !allowedDigits.includes("0") ? 0.4 : 1,
          cursor:
            focusedField === "tel" && !allowedDigits.includes("0")
              ? "not-allowed"
              : "pointer",
        }}
      >
        0
      </Fab>

      <Tooltip title="Sélectionner la quantité">
        <Fab
          className={classes.sideBtnCalcSide}
          tabIndex="-1"
          variant="extended"
          color="secondary"
          onClick={() => handleFieldFocus("qte")}
        >
          <Iconn path={mdiPen} color="black" size="20px" />
        </Fab>
      </Tooltip>
    </div>
  );

  const renderOrderHistory = (orders) => <OrderHistoryDialog orders={orders} />;

  const renderBundleCards = () => (
    <Box sx={{ flexGrow: 0, flexShrink: 0, width: "fit-content" }}>
      {phoneType ? (
        <ScrollBarDynamic id="bdlList" key={"bdlList"}>
          {(phoneType.type === "bundle" ? loadingBundles : loadingCards) ? (
            Array.from(new Array(5)).map((_, i) => (
              <Skeleton
                variant="rect"
                width={248}
                height={140}
                style={{
                  boxShadow: 3,
                  marginBottom: "16px",
                  borderRadius: "10px",
                }}
                key={i}
              />
            ))
          ) : (phoneType.type === "bundle" ? filteredBundles : filteredCards)
              .length > 0 ? (
            (phoneType.type === "bundle" ? filteredBundles : filteredCards).map(
              (product) => (
                <Card
                  key={product.id}
                  className="bdlCards"
                  style={{
                    width: 250,
                    boxShadow: 3,
                    margin: "0 16px",
                    marginBottom: "16px",
                    borderRadius: "10px",
                  }}
                  elevation={4}
                >
                  <CardMedia
                    component="img"
                    height="140"
                    image={
                      phoneType.type === "bundle"
                        ? param.imgBdl + product.bundleId + ".png"
                        : param.imgDigital + "prod_" + product.liberoId + ".png"
                    }
                    alt={
                      phoneType.type === "bundle"
                        ? product.nomFr
                        : product.digit
                    }
                    style={{ padding: 0, cursor: "pointer" }}
                    onClick={() =>
                      phoneType.type === "bundle"
                        ? handleOrder(product)
                        : handleOpenIdoomDialog(product, phoneNumber)
                    }
                  />
                </Card>
              ),
            )
          ) : (
            <div id="bdlNodata" className="flex-wrapper-centered">
              <NoTableDataComponent message="Aucun produit correspond aux filtres" />
            </div>
          )}
        </ScrollBarDynamic>
      ) : (
        <Box
          id="bdlList"
          style={{ alignItems: "center", display: "flex", padding: "20px" }}
        >
          <Typography variant="body2" align="center">
            {phoneNumber
              ? "Numéro non reconnu"
              : "Veuillez insérer le numéro d'abord."}
            <br />
            Formats acceptés : <br />
            05xx xx xx xx / 06xx xx xx xx / 07xx xx xx xx / 02x xx xx xx / 03x
            xx xx xx / 213xxxxxxxxx
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderSabbaqliDialog = (dialog) => (
    <Dialog
      key={dialog.id}
      open={dialog.open}
      onClose={() => closeSabbaqliDialog(dialog.id)}
      PaperProps={{
        style: {
          padding: "24px 32px",
          borderRadius: "12px",
          textAlign: "center",
          minWidth: "300px",
          maxWidth: "400px",
        },
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        style={{ gap: "16px" }}
      >
        <Typography variant="h5" gutterBottom>
          Sabbaqli
        </Typography>
        <TransfertSvg width="100%" height={"120px"} color="red" />
        <Card
          className="bdlCards"
          style={{
            width: "100%",
            boxShadow: 3,
            minHeight: "200px",
            borderRadius: "15px",
          }}
          elevation={4}
        >
          <CardMedia
            key={dialog.bdl + ".png"}
            component="img"
            width="100%"
            height={"auto"}
            minHeight="200px"
            image={param.imgBdl + dialog.bdl + ".png"}
            style={{
              padding: 0,
              objectFit: "contain",
              borderRadius: "15px",
            }}
          />
        </Card>

        <Typography
          style={{
            paddingTop: "8px",
            color: "var(--primary-main)",
            fontWeight: "600",
          }}
          variant="h4"
          gutterBottom
        >
          {Gfunc.formatPhoneNumber(dialog.num)}
        </Typography>
        <Typography variant="body1" style={{ marginBottom: 16 }}>
          Quantité supplémentaire : <b>{dialog.sbqQte}</b>
        </Typography>

        <Box display="flex" justifyContent="center" style={{ gap: "24px" }}>
          <Button
            variant="contained"
            color="secondary"
            style={{ fontSize: "18px" }}
            onClick={() => handleSabbaqliChoice(dialog.id, 0)}
          >
            {dialog.qte}
          </Button>
          <Button
            variant="contained"
            color="primary"
            style={{ fontSize: "18px" }}
            onClick={() => handleSabbaqliChoice(dialog.id, 1)}
          >
            {dialog.qte + dialog.sbqQte}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );

  const renderFormFields = () => (
    <ValidatorForm
      noValidate
      id="addBdlForm"
      onError={(errors) => console.log(errors)}
    >
      {renderPriceSuggestions()}

      <div className="bdlQte">
        <Box
          sx={{
            display: "grid",
            gap: "8px",
            gridTemplateColumns: "auto 140px",
          }}
        >
          <TextField
            inputRef={telInputRef}
            focused={focusedField === "tel"}
            onFocus={() => handleFieldFocus("tel")}
            onKeyDown={handlePhoneKeyPress}
            onPaste={handlePhonePaste}
            variant="outlined"
            size="small"
            label="Tel"
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
            required
            className="largeText"
            inputProps={{
              pattern: phoneInputProps.pattern,
              maxLength: phoneInputProps.maxLength,
            }}
            InputProps={{
              style: {
                fontSize: "14px",
              },
            }}
            //placeholder={phoneInputProps.placeholder}
            error={errorNumber}
            helperText={
              phoneNumber.length === 0
                ? "Premier chiffre: 0 ou 2"
                : phoneNumber.length < 3
                  ? `Chiffres autorisés: ${allowedDigits.join(", ")}`
                  : `${phoneNumber.length}/${phoneInputProps.maxLength}`
            }
            FormHelperTextProps={{
              style: {
                fontSize: "0.7rem",
                marginTop: "4px",
              },
            }}
          />
          <Box sx={{ display: "flex", gap: "8px" }}>
            <TextField
              inputRef={qteInputRef}
              focused={focusedField === "qte"}
              onFocus={() => handleFieldFocus("qte")}
              variant="outlined"
              size="small"
              label={LABEL_TEXT}
              value={quantity}
              onChange={handleQuantityChange}
              className="largeText"
              required
              inputProps={{
                pattern: "[0-9]*",
              }}
            />
            <Fab
              style={{ padding: 0 }}
              tabIndex="-1"
              variant="extended"
              color="secondary"
              onClick={handleClearCurrentField}
            >
              <Iconn path={mdiDelete} color="black" size="20px" />
            </Fab>
          </Box>
          <Autocomplete
            disableClearable
            className="smallAuto"
            id="iTick"
            onChange={(e, newValue) => {
              if (newValue.tiketId != null) setPayer(false);
              else setPayer(true);
              setTicket(newValue);
            }}
            value={ticket}
            options={[
              { tiketId: null, numero: "Nouveau ticket" },
              ...props.tickets,
            ]}
            getOptionLabel={(option) => {
              if (!option) return "";
              return `${option.heure ? option.heure + " " : ""}${
                option.numero || ""
              }`;
            }}
            renderOption={(option) =>
              `${option.heure ? option.heure + "   " : ""}[ ${option.numero} ]`
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Ticket"
                margin="dense"
                variant="outlined"
                size="small"
                required
              />
            )}
          />
          <FormControlLabel
            control={
              <Checkbox
                disabled={ticket.tiketId}
                checked={payer}
                onChange={(e) => setPayer(e.target.checked)}
                color="primary"
              />
            }
            label="Payer"
          />
        </Box>
      </div>

      {renderNumberPad()}
      {renderControlPad()}
    </ValidatorForm>
  );

  return (
    <>
      {Gfunc.irahaUser.sesIrh ? (
        <RaccBtnStatic
          id="addbdlrac"
          tip="Bundles"
          className={classes.racAir}
          onClick={handleOpenDialog}
          text={param.appBdlsName}
          path="mdiCardsOutline"
          iconColor="white"
          digital={props.digital}
          racc="F10"
          disabled={props.disabled}
        />
      ) : null}

      <Dialog
        open={isDialogOpen}
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-dialog-title"
        PaperProps={paperPropsBigFixedBdlVnt}
        id="addBdldiag"
      >
        <AlertCompo
          severity={alertState.severity}
          open={alertState.open}
          setOpen={(open) => setAlertState((prev) => ({ ...prev, open }))}
          message={alertState.message}
          dialg={true}
        />

        <DialogTitle
          className={classes.topDivTitle}
          onClose={handleCloseDialog}
        >
          <div className="DialogTitle">
            <Iconn path={mdiCardsOutline} size={1} />
            <label>{ADD_TITLE}</label>
          </div>
        </DialogTitle>

        <DialogContent id="addbdlForm">
          {renderOrderHistory(orders)}
          <Box sx={{ overflow: "hidden", width: "685px" }}>
            <Box sx={{ padding: "0 12px" }}>
              <ScrollBarDynamic
                key="filters-bar"
                ornt="horizontal"
                style={{
                  width: "100%",
                  minHeight: "45px",
                  height: "45px",
                  background: "#efefef",
                  alignItems: "center",
                  padding: "0 8px",
                  borderRadius: "20px",
                  maxWidth: "none",
                }}
              >
                {filteredFilters.length > 0 ? (
                  phoneType ? (
                    <Box sx={{ width: "100%", display: "flex", gap: 8 }}>
                      {filteredFilters.map((e, index) => (
                        <CardMedia
                          component="img"
                          key={"filter-" + index}
                          image={param.imgBdl + "filtres/" + e.img}
                          onClick={() => handleSelectBundle(e.keySearch)}
                          style={{
                            height:
                              activeBundleFilter === e.keySearch
                                ? "35px"
                                : "30px",
                            width: "auto",
                            cursor: "pointer",
                            opacity:
                              activeBundleFilter === e.keySearch ||
                              !activeBundleFilter
                                ? "1"
                                : "0.6",
                            borderRadius: "4px",
                          }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        paddingTop: "6px",
                        justifyContent: "center",
                      }}
                    >
                      <Typography variant="body2">
                        Veuillez insérer le numero d'abord.
                      </Typography>
                    </Box>
                  )
                ) : (
                  <></>
                )}
              </ScrollBarDynamic>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: "8px",
                padding: "8px",
                height: "95%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  width: { sm: "380px", md: "380px" },
                  overflow: "hidden",
                  paddingRight: "8px",
                }}
              >
                {renderFormFields()}
              </Box>
              {renderBundleCards()}
              {renderIdoomDialog()}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Render all sabbaqli dialogs */}
      {sabbaqliDialogs.map(renderSabbaqliDialog)}
    </>
  );
}
